import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";
import ServiceCategoriesTable from "./components/CategoriesTable";
import { useServiceCategories } from "./hooks/categories.hooks";
import { ServiceCategoriesFilters } from "@/services/interfaces/IServiceCategoryService";
import { ServiceCategoryDialog } from "./components/ServiceCategoryDialog";

const PER_PAGE = 10;
type SortBy = NonNullable<ServiceCategoriesFilters["sortBy"]>;

export const ServiceCategoriesPage: React.FC = () => {
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const { hotelId } = useHotel();
  const filters: ServiceCategoriesFilters = useMemo(() => {
    return {
      page: currentPage,
      limit: PER_PAGE,
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
      hotelId,
    };
  }, [currentPage, sortBy, sortOrder, hotelId]);

  const { data, isLoading } = useServiceCategories(filters);
  const serviceCategories = data?.items ?? [];
  const totalPages = data?.totalPages;

  const handleSort = (field: string) => {
    const newField = field as SortBy;
    if (sortBy !== newField) {
      setSortBy(newField);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  return (
    <div className="flex flex-col gap-12 p-12 max-w-[1344px] mx-auto">
      <div className="flex flex-col gap-6">
        <div className="flex flex-row items-center justify-between min-h-12">
          <div className="flex flex-row items-center gap-x-4 h-full"></div>

          <div className="flex flex-row items-center gap-x-4">
            <ServiceCategoryDialog>
              <Button variant="icon-text">
                <Plus className="size-5" />
                {t(
                  "services.categories.page.createServiceCategoryCta",
                  "Create Category",
                )}
              </Button>
            </ServiceCategoryDialog>
          </div>
        </div>

        <ServiceCategoriesTable
          data={serviceCategories}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages || 1}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default ServiceCategoriesPage;
